import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { AlertCircle, Shield, Info } from 'lucide-react';
import UserAvatar from './UserAvatar';

const LEVELS = [
  { value: 'L', label: 'Lead',   tint: 'lvl-l', description: 'Authoring authority (issues, owns the work)'   },
  { value: 'A', label: 'Assist', tint: 'lvl-a', description: 'Assisting / co-authoring authority'           },
  { value: 'V', label: 'Verify', tint: 'lvl-v', description: 'Verification authority (independent review)' },
  { value: 'E', label: 'Exempt', tint: 'lvl-e', description: 'Exempt — outside this subject\'s competence' },
];

const ROLE_ORDER = ['HDO', 'ChiefEng', 'Structures', 'ConfigCompliance', 'IndepMonVerif', 'ExternalCVE'];

function initials(name, email) {
  const src = (name || email || '?').trim();
  const parts = src.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function CompetenceMatrix({ user }) {
  const [persons, setPersons] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [permissionError, setPermissionError] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [peopleRes, subjectsRes] = await Promise.all([
        axios.get('/people'),
        axios.get('/subjects'),
      ]);
      // Sort persons by primary role order, then by name
      const sortedPersons = [...(peopleRes.data || [])].sort((a, b) => {
        const ai = ROLE_ORDER.indexOf(a.primaryRole);
        const bi = ROLE_ORDER.indexOf(b.primaryRole);
        if (ai !== bi) return ai - bi;
        return (a.user?.name || '').localeCompare(b.user?.name || '');
      });
      setPersons(sortedPersons);
      setSubjects((subjectsRes.data || []).sort((a, b) => a.number - b.number));
    } catch (err) {
      if (err.response?.status === 403) {
        setPermissionError(true);
        setError(err.response.data?.error || 'You don\'t have access to this section.');
      } else {
        setError(err.response?.data?.error || 'Failed to load competence matrix.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build a quick lookup table: marks[personId][subjectId] = level
  const buildMarkMap = (peopleList) => {
    const out = {};
    for (const p of peopleList) {
      out[p._id] = {};
      for (const m of (p.competenceMarks || [])) {
        if (m.subject?._id) out[p._id][m.subject._id] = m.level;
      }
    }
    return out;
  };
  const [markMap, setMarkMap] = useState({});
  useEffect(() => { setMarkMap(buildMarkMap(persons)); }, [persons]);

  const setMark = async (personId, subjectId, newLevel) => {
    // Optimistic update
    const previous = markMap[personId]?.[subjectId] ?? null;
    setMarkMap(prev => ({
      ...prev,
      [personId]: { ...(prev[personId] || {}), [subjectId]: newLevel },
    }));
    try {
      await axios.put(`/people/${personId}/competence/${subjectId}`, { level: newLevel || null });
    } catch (err) {
      // Rollback on failure
      setMarkMap(prev => ({
        ...prev,
        [personId]: { ...(prev[personId] || {}), [subjectId]: previous },
      }));
      setError(err.response?.data?.error || 'Failed to update mark.');
      // Clear after a few seconds
      setTimeout(() => setError(''), 5000);
    }
  };

  if (loading) return (
    <div className="loading-screen" style={{ minHeight: 'auto', padding: '60px 0' }}>
      <div className="loading-spinner"></div>
    </div>
  );

  if (permissionError) return (
    <div className="dasa-perm-error">
      <AlertCircle size={28} strokeWidth={1.5} />
      <h3>Not enrolled in DAS</h3>
      <p>{error}</p>
      <p className="dasa-perm-hint">
        Ask the HDO to enrol you as a Person in this organisation if you should have access.
      </p>
    </div>
  );

  return (
    <div className="dasa-section">
      <header className="dasa-section-header">
        <h2>{persons.length} {persons.length === 1 ? 'person' : 'people'} × {subjects.length} subjects</h2>
        <MatrixLegend />
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="cm-wrap">
        <div className="cm-grid" style={{ '--n-subjects': subjects.length }}>
          {/* Top-left empty corner */}
          <div className="cm-corner"></div>

          {/* Subject column headers */}
          {subjects.map(s => (
            <div key={s._id} className={`cm-subj-head cm-subj-${s.domain}`} title={`${s.number}. ${s.name}`}>
              <span className="cm-subj-num">#{s.number}</span>
              <span className="cm-subj-name">{s.name}</span>
            </div>
          ))}

          {/* Rows */}
          {persons.map(person => (
            <React.Fragment key={person._id}>
              <div className="cm-person-head">
                <UserAvatar user={person.user} className="cm-avatar" />
                <span className="cm-person-block">
                  <span className="cm-person-name">{person.user?.name || person.user?.email}</span>
                  <span className="cm-person-role">{person.primaryRole}</span>
                </span>
              </div>
              {subjects.map(subject => (
                <MatrixCell
                  key={`${person._id}-${subject._id}`}
                  level={markMap[person._id]?.[subject._id]}
                  onSet={(newLevel) => setMark(person._id, subject._id, newLevel)}
                />
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      <p className="dasa-foot-note">
        <Shield size={11} /> Click any cell as HDO to set the mark. Changes save immediately.
        Edits by non-HDO users will be rejected by the server.
      </p>
    </div>
  );
}

function MatrixLegend() {
  return (
    <div className="cm-legend">
      {LEVELS.map(l => (
        <span key={l.value} className={`cm-legend-item cm-${l.tint}`} title={l.description}>
          <span className="cm-chip">{l.value}</span> {l.label}
        </span>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MatrixCell — displays current mark; click opens picker popover
// ─────────────────────────────────────────────────────────────────

function MatrixCell({ level, onSet }) {
  const [showPicker, setShowPicker] = useState(false);
  const cellRef = useRef(null);

  // Click-outside / Escape to close
  useEffect(() => {
    if (!showPicker) return;
    const handleClick = (e) => {
      if (cellRef.current && !cellRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
    const handleKey = (e) => { if (e.key === 'Escape') setShowPicker(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [showPicker]);

  const handlePick = (newLevel) => {
    setShowPicker(false);
    onSet(newLevel);
  };

  const levelTint = LEVELS.find(l => l.value === level)?.tint;

  return (
    <div className="cm-cell-wrap" ref={cellRef}>
      <button
        type="button"
        className={`cm-cell ${level ? 'has-mark' : ''}`}
        onClick={() => setShowPicker(s => !s)}
        aria-label={level ? `Mark: ${level}. Click to change.` : 'Empty. Click to set mark.'}
      >
        {level
          ? <span className={`cm-chip cm-${levelTint}`}>{level}</span>
          : <span className="cm-cell-empty">·</span>
        }
      </button>

      {showPicker && (
        <div className="cm-picker" role="menu">
          {LEVELS.map(l => (
            <button
              key={l.value}
              type="button"
              role="menuitem"
              className={`cm-picker-opt cm-${l.tint} ${level === l.value ? 'is-current' : ''}`}
              onClick={() => handlePick(l.value)}
              title={l.description}
            >
              <span className={`cm-chip cm-${l.tint}`}>{l.value}</span>
              <span className="cm-picker-label">{l.label}</span>
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            className={`cm-picker-opt cm-picker-clear ${!level ? 'is-current' : ''}`}
            onClick={() => handlePick(null)}
          >
            <span className="cm-chip cm-chip-empty">·</span>
            <span className="cm-picker-label">Clear</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default CompetenceMatrix;
